<template>
  <div v-if="mostrarKeyboard" class="keyboard-overlay" @click="fecharTeclado">
    <div
      class="keyboard-container tw-p-3 tw-rounded-lg tw-flex tw-flex-col tw-shadow-2xl tw-bg-black tw-bg-opacity-80"
      @click.stop
    >
      <div class="tw-flex tw-justify-end tw-mb-2">
        <button
          @click="fecharTeclado"
          class="tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors tw-flex-shrink-0 mb-2"
          title="Fechar teclado"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="tw-h-6 tw-w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="white"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <div class="simple-keyboard tw-flex-1"></div>
    </div>
  </div>
</template>

<script>
import Keyboard from "simple-keyboard";
import "simple-keyboard/build/css/index.css";

/**
 * Componente de teclado numérico virtual.
 * eventos:
 * - input: Emite o valor atual do teclado.
 * - fechar: Emite quando o teclado é fechado.
 * - focusInput: Emite quando o teclado foca o input associado.
 */
export default {
  name: "NumericKeyboard",
  props: {
    value: {
      type: String,
      default: "",
    },
    mostrar: {
      type: Boolean,
      default: false,
    },
    inputRef: {
      default: null,
    },
    aplicarAoEnter: {
      type: Function,
      default: null,
    },
    limitCharacters: {
      type: Number,
      default: 6,
    },
  },
  data() {
    return {
      instanciaKeyboard: null,
      mostrarKeyboard: false,
      bloqueioDeGesto: null,
      timeoutDoGesto: null,
    };
  },
  watch: {
    mostrar(newVal) {
      this.mostrarKeyboard = newVal;
      if (newVal) {
        this.$nextTick(() => {
          this.inicializarKeyboard();
        });
      } else {
        this.destruirKeyboard();
      }
    },
    value(newVal) {
      if (this.instanciaKeyboard) {
        this.instanciaKeyboard.setInput(newVal);
      }
    },
  },
  methods: {
    inicializarKeyboard() {
      if (this.instanciaKeyboard) return;

      let self = this;
      this.instanciaKeyboard = new Keyboard(
        ".simple-keyboard",
        {
          onChange: (input) => self.onKeyboardChange(input),
          onKeyPress: (button) => self.onKeyboardKeyPress(button),
          layout: {
            default: ["1 2 3", "4 5 6", "7 8 9", "{backspace} 0 {enter}"],
          },
          display: {
            "{backspace}": "⌫",
            "{enter}": "Enter",
          },
        },
        {
          debug: false,
          disableCaretPositioning: false,
        }
      );
      this.instanciaKeyboard.setInput(this.value);
    },
    onKeyboardChange(input) {
      if (this.inputRef) {
        this.inputRef.focus();
      }
      this.$emit("focusInput");
      const inputLimitado = input.slice(0, this.limitCharacters);
      if (input !== inputLimitado) {
        this.instanciaKeyboard.setInput(inputLimitado);
      }
      this.$emit("input", inputLimitado);
    },
    onKeyboardKeyPress(button) {
      if (button === "{enter}") {
        if (this.aplicarAoEnter) {
          this.aplicarAoEnter();
          this.fecharTecladoAoFimDoGesto();
        }
      }
    },
    /**
     * O simple-keyboard registra a tecla já no "pointerdown". Fechar o teclado
     * nesse instante tira o overlay do DOM antes de o dedo sair da tela: o
     * navegador refaz o hit-test no fim do toque e entrega o clique sintetizado
     * para o que estava atrás do teclado — na tela de pagamento, o rodapé fixo.
     * Por isso mantemos o teclado montado até o gesto terminar e engolimos esse
     * clique final antes de fechar.
     */
    fecharTecladoAoFimDoGesto() {
      if (this.bloqueioDeGesto) return;

      this.bloqueioDeGesto = (evento) => {
        evento.preventDefault();
        evento.stopPropagation();
        this.encerrarBloqueioDeGesto();
      };
      document.addEventListener("click", this.bloqueioDeGesto, true);

      // Se o toque for cancelado (dedo arrastado para fora, pointercancel), o
      // clique final nunca chega e o teclado ficaria preso aberto.
      this.timeoutDoGesto = setTimeout(
        () => this.encerrarBloqueioDeGesto(),
        400
      );
    },
    encerrarBloqueioDeGesto() {
      if (!this.bloqueioDeGesto) return;
      this.limparBloqueioDeGesto();
      this.fecharTeclado();
    },
    limparBloqueioDeGesto() {
      clearTimeout(this.timeoutDoGesto);
      this.timeoutDoGesto = null;
      if (this.bloqueioDeGesto) {
        document.removeEventListener("click", this.bloqueioDeGesto, true);
        this.bloqueioDeGesto = null;
      }
    },
    caretRemove() {
      if (this.instanciaKeyboard) {
        this.instanciaKeyboard.setCaretPosition(
          this.instanciaKeyboard.getCaretPosition() - 1
        );
      }
    },
    fecharTeclado() {
      this.destruirKeyboard();
      this.$emit("fechar");
    },
    destruirKeyboard() {
      if (this.instanciaKeyboard) {
        this.instanciaKeyboard.destroy();
        this.instanciaKeyboard = null;
      }
      this.mostrarKeyboard = false;
    },
  },
  beforeDestroy() {
    this.limparBloqueioDeGesto();
    this.destruirKeyboard();
  },
};
</script>

<style scoped>
.keyboard-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  background-color: transparent;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 9999;
  pointer-events: none;
}

.keyboard-container {
  width: 100%;
  max-width: 100%;
  pointer-events: auto;
  padding: 20px;
  box-sizing: border-box;
}

/* Estilos customizados para o keyboard virtual */
:deep(.simple-keyboard) {
  width: 100%;
}

:deep(.hg-theme-default) {
  background-color: rgba(230, 230, 230, 0.9);
  border-radius: 8px;
  padding: 13px;
  width: 10;
}

:deep(.hg-row) {
  margin-bottom: 8px;
}

:deep(.hg-button) {
  height: 50px;
  font-size: 20px;
  font-weight: 500;
  background: white;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  line-height: 45px;
}

:deep(.hg-button:active) {
  background: #da1884;
  color: white;
  border-color: #da1884;
}

:deep(.hg-button.hg-standardBtn) {
  flex-grow: 1;
}

:deep(.hg-row:last-child .hg-button) {
  flex-grow: 1;
}

:deep(.hg-button[data-skbtn="{backspace}"]) {
  font-size: 16px;
  font-weight: bold;
  width: 30%;
  background-color: #da1884;
  color: white;
  border-color: #da1884;
}

:deep(.hg-button[data-skbtn="0"]) {
  width: 30%;
}

:deep(.hg-button[data-skbtn="{enter}"]) {
  font-size: 22px;
  width: 30%;
  background-color: #da1884;
  color: white;
  border-color: #da1884;
}

:deep(.hg-button:active[data-skbtn="{backspace}"]),
:deep(.hg-button:active[data-skbtn="{enter}"]) {
  background: white;
  color: #da1884;
  border-color: white;
}
</style>
